/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  formatNumber,
  formatPercent,
  formatCurrency,
  formatLargeNumber,
  formatLargeCurrency,
} from "../../utils/format-number";
import { ChartComponentParams } from "@/types/chart";
import { ChartOptions } from "chart.js";

const formatTooltip = ({
  context,
  formatter,
}: {
  context: any;
  formatter: (value: string | number) => string;
}) => {
  const label = context.dataset.label;
  const value = context.parsed.y;
  return `${label}: ${formatter(value)}`;
};

/**
 * Converts the payload to options for the chart
 * @param title The title of the chart
 * @param valueFormatType The format type of the value
 * @returns The options for the chart
 */
export const convertValueFormatTypeToOptions = ({
  valueFormatType,
}: {
  valueFormatType: ChartComponentParams["valueFormatType"];
}): ChartOptions<"bar" | "line"> => {
  const optionsByValueFormatType: Record<
    ChartComponentParams["valueFormatType"],
    ChartOptions<"bar" | "line">
  > = {
    none: {},
    number: {
      scales: {
        y: {
          ticks: {
            callback: (value: string | number) => formatNumber(value),
          },
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: (context) =>
              formatTooltip({ context, formatter: formatNumber }),
          },
        },
      },
    },
    percent: {
      scales: {
        y: {
          ticks: {
            callback: (value: string | number) => formatPercent(value),
          },
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: (context) =>
              formatTooltip({ context, formatter: formatPercent }),
          },
        },
      },
    },
    currency: {
      scales: {
        y: {
          ticks: {
            callback: (value: string | number) => formatCurrency(value),
          },
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: (context) =>
              formatTooltip({ context, formatter: formatCurrency }),
          },
        },
      },
    },
    "large-number": {
      scales: {
        y: {
          ticks: {
            callback: (value: string | number) => formatLargeNumber(value),
          },
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: (context) =>
              formatTooltip({ context, formatter: formatLargeNumber }),
          },
        },
      },
    },
    "large-currency": {
      scales: {
        y: {
          ticks: {
            callback: (value: string | number) => formatLargeCurrency(value),
          },
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: (context) =>
              formatTooltip({ context, formatter: formatLargeCurrency }),
          },
        },
      },
    },
  };

  return optionsByValueFormatType[valueFormatType];
};
